import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// URL events can also be delivered explicitly to the exported Android activity.
// Treat the payload as untrusted: owning a path is not the same as owning its host.
export function deepLinkDestination(value) {
    if (typeof value !== 'string' || !/^(?:https|growdaily|growdailydebug):\/\//i.test(value)
        || /[\u0000-\u0020\\]/.test(value) || /%(?![a-f\d]{2})/i.test(value)) return null;
    try {
        const url = new URL(value);
        if (url.username || url.password || url.port) return null;
        const quickScheme = url.protocol === 'growdaily:' || url.protocol === 'growdailydebug:';
        if (quickScheme) {
            // These schemes are the fixed native camera doors, not auth callbacks.
            if (url.hostname !== 'open' || url.pathname !== '/id' || url.hash) return null;
        } else if (url.protocol !== 'https:' || url.hostname !== 'growdaily.fun') {
            return null;
        }
        // Never hand the router a network-path reference or an encoded separator.
        // Queries remain data; neither `next` nor `redirect` selects this destination.
        if (!/^\/(?![\/\\])/.test(url.pathname) || /%(?:2f|5c)/i.test(url.pathname)) return null;
        const path = `${url.pathname}${url.search}${url.hash}`;
        if (path === '/') return null;
        return { href: url.href, path };
    } catch { return null; }
}

// Handles Android App Links: when the OS opens the native app from a
// https://growdaily.fun/... link (e.g. a magic-link email), Capacitor fires
// `appUrlOpen` for an already running app; getLaunchUrl supplies the URL on
// a cold launch. Both route the SPA to the intended camera or account page.
// Uses the global
// Capacitor bridge (window.Capacitor.Plugins.App) — no npm import needed.
export default function DeepLinkHandler() {
    const navigate = useNavigate();
    const launchRequest = useRef(null);
    const launchHandled = useRef(false);
    const lastOpen = useRef(null);

    useEffect(() => {
        const App = window.Capacitor?.Plugins?.App;
        if (!App?.addListener) return;

        let active = true;
        let handle;
        const open = (data, initial = false) => {
            if (!active || (initial && launchHandled.current)) return;
            const destination = deepLinkDestination(data?.url);
            if (!destination) return;
            try {
                // Path + query (+ hash) — magic links carry the token in ?t=...
                launchHandled.current = true;
                // Some Android launches deliver the same URL through both
                // channels. Suppress that burst, while allowing a later tap
                // on the same shortcut to open the camera again.
                const now = Date.now();
                if (lastOpen.current?.url === destination.href && now - lastOpen.current.at < 1000) return;
                lastOpen.current = { url: destination.href, at: now };
                navigate(destination.path);
            } catch { /* ignore malformed urls */ }
        };
        const sub = App.addListener('appUrlOpen', (data) => open(data));
        Promise.resolve(sub).then((h) => {
            if (active) handle = h;
            else h?.remove?.();
        }).catch(() => {});

        if (!launchRequest.current) {
            launchRequest.current = Promise.resolve().then(() => App.getLaunchUrl?.()).catch(() => null);
        }
        launchRequest.current.then((data) => open(data, true));

        return () => {
            active = false;
            try { handle?.remove?.(); } catch { /* ignore */ }
        };
    }, [navigate]);

    return null;
}
